import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import QRCodeScanner from 'shared/components/QRCodeScanner'
import Button from 'shared/components/ui/button'
import Input from 'shared/components/ui/input'
import { IconBack } from 'shared/components/icons'
import { useEvent } from 'context/EventContext'
import { cn } from 'shared/util/cn'
import { parseScanValue } from 'shared/util/ticketCode'

function ScanCamera() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { setLastScan } = useEvent()
  const [code, setCode] = useState('')
  const [manual, setManual] = useState(false)
  const locked = useRef(false)
  const stopRef = useRef(null)

  useEffect(() => {
    setLastScan({ status: 'pending', code: '', eventId })
  }, [eventId, setLastScan])

  const goResult = (value) => {
    if (!value || locked.current) return
    const parsed = parseScanValue(value)
    if (!parsed?.value) return
    locked.current = true
    try {
      stopRef.current?.()
    } catch {
      // camera may already be stopping
    }
    setLastScan({ status: 'pending', code: parsed.value, eventId })
    navigate(`/events/${eventId}/result`, { replace: true, state: { code: parsed.value } })
  }

  const submitManual = (e) => {
    e.preventDefault()
    goResult(code)
  }

  return (
    <div className="relative min-h-dvh bg-dark text-light">
      <QRCodeScanner
        fullscreen
        autoStart={!manual}
        onScanSuccess={(decoded) => goResult(decoded)}
        onScanError={() => {}}
      >
        {({ start, stop, isRunning }) => {
          stopRef.current = stop
          return (
            <>
              {(!isRunning || manual) && (
                <div className={cn('absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-dark', manual && 'pointer-events-none opacity-0')}>
                  <p className="px-8 text-center text-muted">Camera is the fastest way to admit guests.</p>
                  <Button onClick={start}>Enable camera</Button>
                </div>
              )}
              <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
                <div className="h-56 w-56 rounded-3xl border-2 border-theme shadow-glow" />
              </div>
            </>
          )
        }}
      </QRCodeScanner>

      <div className="absolute inset-x-0 top-0 z-30 flex items-center justify-between p-4">
        <button
          type="button"
          className="flex h-12 w-12 items-center justify-center rounded-2xl bg-dark/70"
          onClick={() => navigate(`/events/${eventId}`)}
        >
          <IconBack className="h-5 w-5" />
        </button>
        <p className="mb-0 text-sm font-semibold">Align QR in the frame</p>
        <span className="w-12" />
      </div>

      <div className="absolute inset-x-0 bottom-0 z-30 space-y-3 bg-gradient-to-t from-dark via-dark/90 to-transparent p-4 pb-8">
        <button
          type="button"
          className="w-full text-center text-sm font-semibold text-primary"
          onClick={() => {
            setManual((open) => {
              const next = !open
              if (next) {
                try {
                  stopRef.current?.()
                } catch {
                  // ignore
                }
              }
              return next
            })
          }}
        >
          {manual ? 'Hide manual entry' : 'Enter 6-character code'}
        </button>
        {manual && (
          <form onSubmit={submitManual} className="flex gap-2">
            <Input
              id="manual-code"
              value={code}
              maxLength={6}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Y3VGAD"
              className={cn('bg-elevated text-center font-mono text-2xl tracking-[0.3em]')}
            />
            <Button type="submit" disabled={code.trim().length < 6}>
              Go
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}

export default ScanCamera
